import { DisclosureSection } from '@components/core-ui/DisclosureSection';
import { TeamCards } from '@components/TeamCards';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { OrganizationTeamResponseDto } from 'app/API';
import { createFetchOrganizationTeamsQueryFn } from 'app/queries/teams/createFetchOrganizationTeamsQueryFn';
import { teamsKeys } from 'app/queries/teams/teamsKeys';
import { useState } from 'react';
import { LoadError } from '../LoadError';
import { sortTeamsByName } from './helpers/sortTeamsByName';
import { Wrapper } from './styles/Wrapper';

const EMPTY_TEAMS: OrganizationTeamResponseDto[] = [];

export const TeamsList = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModalContext();
  const [teamsDisclosureOpen, setTeamsDisclosureOpen] = useState<boolean>(true);
  const { organization } = useAuthContext();
  const organizationId = organization?.id ?? '';
  const teamsListKey = teamsKeys.listsByOrganization(organizationId);

  const teamsQuery = useQuery({
    queryKey: teamsListKey,
    queryFn: createFetchOrganizationTeamsQueryFn(call),
    select: sortTeamsByName,
    enabled: organizationId !== '',
  });
  const teams = teamsQuery.data ?? EMPTY_TEAMS;

  const handleRemoval = (teamId: string) => {
    openModal(ModalNamesEnum.DeleteTeamModal, {
      teamId,
      onSuccess: () => {
        queryClient.setQueryData<OrganizationTeamResponseDto[]>(teamsListKey, (current) =>
          current?.filter((team) => team.id !== teamId),
        );
        void queryClient.invalidateQueries({ queryKey: teamsListKey });
        closeModal();
      },
    });
  };

  const handleAddTeam = () => {
    if (!organization) {
      return;
    }
    openModal(ModalNamesEnum.NewTeamModal, {
      organizationId: organization.id,
      onSuccess: (team: OrganizationTeamResponseDto) => {
        queryClient.setQueryData<OrganizationTeamResponseDto[]>(teamsListKey, (current) =>
          current ? [...current, team] : current,
        );
        void queryClient.invalidateQueries({ queryKey: teamsListKey });
        closeModal();
      },
    });
  };

  const teamsTitle = `Teams${teams.length > 0 ? ` (${teams.length})` : ''}`;

  return (
    <Wrapper className="flex flex-col">
      <div>
        <DisclosureSection
          title={teamsTitle}
          open={teamsDisclosureOpen}
          setOpenState={setTeamsDisclosureOpen}
          loading={teamsQuery.isLoading}
        >
          {teamsQuery.isError ? (
            <LoadError message="Failed to load teams." onRetry={() => void teamsQuery.refetch()} />
          ) : null}
          <TeamCards teams={teams} handleRemoval={(teamId) => handleRemoval(teamId)} addTeam={handleAddTeam} />
        </DisclosureSection>
      </div>
    </Wrapper>
  );
};
